
import { IPlanificationPhase } from '@/entity/planification/planification-phase';
import { IPlanificationPhaseProfile } from '@/entity/planification/planification-phase-profile';
import { IPlanificationPhaseProfileWorkDay } from '@/entity/planification/planification-phase-profile-work-day';
import { IProject } from '@/entity/project/project';
import { IStudio } from '@/entity/project/studio';
import { ICalendarHalfDay, ICalendarProp } from '@/entity/shared/calendar-day-picker';
import { IEmployeeRole } from '@/entity/shared/referential';
import { vxm } from '@/store';
import { planificationApi } from '@/wapi/planification-api';
import CalendarDayPicker from '@c/shared/calendar-day-picker.vue';
import CurrencyInput from '@c/shared/currency-input.vue';
import InputElementMgt from '@c/shared/input-element-mgt.vue';
import InputElement from '@c/shared/input-element.vue';
import { isCallValidAndNotCancelled } from '@t/ajax-wrapper';
import { getEmployeCost } from '@t/cost-utils';
import { formatCurrencyByProject } from '@t/format-currency';
import { moduleApiGraph } from '@t/module-api-graph';
import { NU } from '@t/type';
import { isAfter, isEqual, max, min } from 'date-fns';
import { Component, Prop, Vue } from 'vue-property-decorator';
import PeriodSelector from './periodSelector.vue';
import ProfileInformation from './scheduler-modal-component/profile-information.vue';
import ProfileItem from './scheduler-modal-component/profile-item.vue';

@Component({
    components: {
        InputElementMgt,
        InputElement,
        CalendarDayPicker,
        CurrencyInput,
        ProfileInformation,
        ProfileItem,
        PeriodSelector
    }
})
export default class SchedulerModal extends Vue {
    @Prop({ required: true }) currentPhase!: IPlanificationPhase;
    @Prop({ required: true }) selectedIndexProfile!: number;
    @Prop({ required: true }) planificationPhases!: NU<IPlanificationPhase[]>;
    @Prop({ required: true }) studios!: NU<IStudio[]>;

    get forcedKeyUpdate() {
        return (
            `${this.selectedPlanificationPhaseToUpdate.id}_` +
            `${this.selectedProfil?.employeeId}_` +
            `${this.selectedProfil?.studioId}_` +
            `${this.selectedProfil?.employeeRoleId}_` +
            `${this.schedulerScenarioVersion}`
        );
    }

    get canShowProfil() {
        const item = this.selectedPlanificationPhaseToUpdate;
        return this.selectedProfil && item && item.planificationPhaseProfiles.length > 0;
    }

    get getDefaultMonth() {
        return this.selectedProfil && this.selectedProfil.calendarPropWorkDays
            ? this.selectedProfil.calendarPropWorkDays.defaultMonth
            : this.defaultDate.getMonth();
    }

    get getDefaultYear() {
        return this.selectedProfil && this.selectedProfil.calendarPropWorkDays
            ? this.selectedProfil.calendarPropWorkDays.defaultYear
            : this.defaultDate.getFullYear();
    }

    get isSaveValid() {
        return this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.some((p) =>
            p.planificationPhaseProfileWorkDays === undefined ? false : p.planificationPhaseProfileWorkDays.length === 0
        );
    }

    get defaultSelected(): any[] | null {
        return this.schedulerScenarioVersion > -1 &&
            Array.isArray(this.selectedProfil?.calendarPropWorkDays?.defaultSelected)
            ? this.selectedProfil!.calendarPropWorkDays.defaultSelected
            : null;
    }

    private defaultDate: Date = new Date();
    private isCalendarOpen: boolean = false;
    private changeOpeningDate: { oldDate: Date; newDate: Date } = {} as { oldDate: Date; newDate: Date };
    promiseSave: boolean = false;
    isAddProfileBouttonClicked: boolean = false;
    selectedPlanificationPhaseToUpdate: IPlanificationPhase = { ...this.currentPhase };
    // Petite astuce pour forcer le composant à se mettre à jour.
    schedulerScenarioVersion: number = 0;

    private showValidation: boolean = false;
    private validationObject: Array<{ profil: boolean; studio: boolean }> = [];

    // Nouveautés
    selectedProfil: IPlanificationPhaseProfile | null = null;
    selectedProfilIndex: number = -1;
    presetFirstUser() {
        const arrayLength = this.currentPhase.planificationPhaseProfiles.length;
        let index = -1;
        if (this.selectedIndexProfile > -1 && this.selectedIndexProfile <= arrayLength) {
            index = this.selectedIndexProfile;
        } else if (arrayLength > 0) {
            index = 0;
        }
        if (index > -1) {
            this.selectedProfil = this.currentPhase.planificationPhaseProfiles[index];
            this.selectedProfilIndex = index;
        }
    }

    selectProfil(profil: IPlanificationPhaseProfile, index: number) {
        this.selectedProfil = profil;
        this.selectedProfilIndex = index;
    }

    onDeleteProfil() {
        const profiles = this.currentPhase.planificationPhaseProfiles;

        if (profiles.length === 0) {
            this.selectedProfilIndex = -1;
            this.selectedProfil = null;
            return;
        }

        const newIndex = this.selectedProfilIndex < profiles.length ? this.selectedProfilIndex : profiles.length - 1;
        this.selectedProfilIndex = newIndex;
        this.selectedProfil = profiles[newIndex];
    }

    get isReadonly(): boolean {
        return !vxm.user.isStudioManager && !vxm.user.isAdmin && !vxm.user.isCP;
    }

    get selectedProject(): NU<IProject> {
        return vxm.project.dropdownProject;
    }

    get employeeRoles(): NU<IEmployeeRole[]> {
        return vxm.referential.employeeRoles;
    }

    async mounted() {
        await this.loadDisplayName();
        this.presetFirstUser();
    }

    async savePhase(item: IPlanificationPhase): Promise<void> {
        this.validatePhases();
        if (this.showValidation === false) {
            await this.savePlanificationPhases();
        }
    }

    private validatePhases() {
        this.showValidation = false;
        this.validationObject = [];
        if (
            this.selectedPlanificationPhaseToUpdate != null &&
            this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles &&
            this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.length > 0 &&
            !this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.some(
                (p) => p.planificationPhaseProfileWorkDays.length === 0
            )
        ) {
            const validation: Array<{ profil: boolean; studio: boolean }> = [];
            this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.forEach((x) => {
                if (x.employeeId != null && x.employeeId.length > 16) {
                    validation.push({ profil: false, studio: false });
                } else {
                    const obj = { profil: x.employeeRoleId == null, studio: x.studioId == null };
                    validation.push(obj);
                    if (obj.profil === true || obj.studio === true) {
                        this.showValidation = true;
                    }
                }
            });
            this.validationObject = validation;
        }
    }

    applySchedulerScenario(workoutdays: ICalendarHalfDay[]) {
        if (this.selectedProfil && this.selectedProfilIndex > -1) {
            this.onCollectionChange(workoutdays, this.selectedProfil, this.selectedProfilIndex);
            this.schedulerScenarioVersion++;
        }
    }

    async savePlanificationPhases(): Promise<void> {
        this.promiseSave = true;
        const callData = await planificationApi.patchPhases(this.planificationPhases ?? []);
        if (isCallValidAndNotCancelled(callData)) {
            this.promiseSave = false;
            this.$bvToast.toast('Enregistrement effectué avec succès', {
                title: 'Planification',
                variant: 'success',
                solid: true
            });
            this.$emit('get-planification-phases', this.selectedProject?.id ?? 0);
            this.$emit('on-close-modal');
        }
    }

    private async selectedUserEvent(item: { event: CustomEvent; profile: IPlanificationPhaseProfile; index: number }) {
        if (item.event && item.event.detail && item.event.detail.length > 0 && item.event.detail[0]) {
            item.profile.employeeId = item.event.detail[0].id;
            item.profile.employeeName = item.event.detail[0].displayName;
            if (item.profile.employee) {
                item.profile.employee.employeeDisplayName = item.event.detail[0].displayName;
            }
            this.setDateforProfile(item.profile, item.index);
            this.setOpeningAndClosingDateForPhase(this.selectedPlanificationPhaseToUpdate);
            this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles[item.index].employeeName =
                item.profile.employeeName;
            this.selectedPlanificationPhaseToUpdate = { ...this.selectedPlanificationPhaseToUpdate };
        } else {
            item.profile.employeeId = null;
            item.profile.employee = null as any;
            item.profile.totalRate = 0;
        }
        this.setProfileTotalRate(item.profile, this.selectedPlanificationPhaseToUpdate, item.index);
    }

    addProfile(): void {
        const profile = {} as IPlanificationPhaseProfile;
        this.isAddProfileBouttonClicked = true;
        profile.employeeId = null;
        profile.planificationPhaseId = this.selectedPlanificationPhaseToUpdate.id;
        profile.totalRate = 0;
        profile.employeeRoleLabel = '';
        profile.selectedDayTypes = ['MOR', 'AFT'];
        profile.planificationPhaseProfileWorkDays = [];
        this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.push(profile);
        this.selectProfil(profile, this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.length - 1);
    }

    async loadDisplayName() {
        const ids = this.currentPhase.planificationPhaseProfiles
            .map((pp) => pp.employeeId)
            .filter((p) => p && p.length > 0);
        const employeesWithNames = await Promise.all(
            ids.map(async (id) => ({ id, name: await this.getEmployeeName(id) }))
        );
        this.currentPhase.planificationPhaseProfiles?.forEach(async (pp) => {
            if (pp.employee && !pp.employee.employeeDisplayName) {
                const obj = employeesWithNames.find((n) => pp.employee && n.id === pp.employeeId);
                if (obj) {
                    pp.employee.employeeDisplayName = obj.name;
                }
            }
        });
    }

    isInFutur(date: Date) {
        return isAfter(new Date(date), new Date());
    }

    setPhaseToUpdate(phaseId: number) {
        if (this.planificationPhases && this.planificationPhases.length > 0 && phaseId != null) {
            const phase = this.planificationPhases.find((x) => x.id === phaseId);
            if (phase) {
                this.selectedPlanificationPhaseToUpdate = phase;
                (this.$refs['plan-modal'] as any).show();
            }
        }
    }

    async getEmployeeName(employeeId: NU<string>): Promise<string> {
        const result = await moduleApiGraph.Client.api('/users/' + employeeId)
            .select('displayName')
            .get()
            .then((value) => value.displayName);
        return result;
    }

    private selectedOpeningDates: Date[] = [];

    private async changeContextDate(context) {
        this.selectedOpeningDates.push(context.selectedDate);
        const planificationToUpdate = this.planificationPhases?.find(
            (p) => p.id === this.selectedPlanificationPhaseToUpdate.id
        );
        if (
            this.isCalendarOpen &&
            this.selectedOpeningDates.length > 1 &&
            this.selectedOpeningDates[this.selectedOpeningDates.length - 1].setHours(0, 0, 0, 0) !==
                this.selectedOpeningDates[this.selectedOpeningDates.length - 2].setHours(0, 0, 0, 0) &&
            planificationToUpdate
        ) {
            this.$nextTick(async () => {
                var dayNew = new Date(this.changeOpeningDate.newDate);
                dayNew.setHours(0, 0, 0, 0);

                var dayOld = new Date(this.changeOpeningDate.oldDate);
                dayOld.setHours(0, 0, 0, 0);

                this.promiseSave = true;
                var slided = await planificationApi.slidePhases(
                    planificationToUpdate.planificationPhaseProfiles,
                    dayOld,
                    dayNew
                );

                if (isCallValidAndNotCancelled<IPlanificationPhaseProfile[]>(slided)) {
                    this.setPhaseToUpdate(planificationToUpdate.id);
                    planificationToUpdate?.planificationPhaseProfiles?.forEach((item, index) => {
                        this.promiseSave = false;
                        const ppp = (slided.datas as IPlanificationPhaseProfile[])[index];

                        ppp.planificationPhaseProfileWorkDays.forEach((day) => {
                            const currentDate = new Date(day.date);
                            currentDate.setHours(0, 0, 0, 0);
                            day.date = currentDate;
                        });

                        item.planificationPhaseProfileWorkDays = ppp.planificationPhaseProfileWorkDays;
                        this.moveWorkDatesForModal(item, index);
                    });
                }
            });
        }
    }

    moveWorkDatesForModal(profile: IPlanificationPhaseProfile, index: number) {
        const dates = profile.planificationPhaseProfileWorkDays?.map((x) => new Date(x.date));
        profile.startDate = min(dates);
        profile.endDate = max(dates);
        const calendarPropWorkDays = {} as ICalendarProp;
        calendarPropWorkDays.defaultMonth = new Date(profile.startDate)?.getMonth() ?? 0;
        calendarPropWorkDays.defaultYear = new Date(profile.startDate)?.getFullYear() ?? 0;
        if (profile.endDate) {
            calendarPropWorkDays.defaultSelected = [];
            profile.planificationPhaseProfileWorkDays.forEach((item) => {
                const currentDate = new Date(item.date);
                currentDate.setHours(0, 0, 0, 0);
                item.date = currentDate;
                const dayNum = currentDate.getDay();
                if (
                    dayNum !== 0 &&
                    dayNum !== 6 &&
                    (profile.selectedWeekDays === undefined ||
                        profile.selectedWeekDays.length === 0 ||
                        profile.selectedWeekDays?.find((x) => x === dayNum) !== undefined)
                ) {
                    calendarPropWorkDays.defaultSelected.push({
                        year: currentDate.getFullYear(),
                        month: currentDate.getMonth(),
                        day: currentDate.getDate(),
                        dayPartId: item.dayPartTypeId
                    } as ICalendarHalfDay);
                }
            });
        }

        if (
            this.selectedPlanificationPhaseToUpdate != null &&
            this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles.length >= index
        ) {
            const x = this.selectedPlanificationPhaseToUpdate?.planificationPhaseProfiles[index];
            x.calendarPropWorkDays = calendarPropWorkDays;
            this.onCollectionChange(calendarPropWorkDays.defaultSelected, x, index);
        }
    }

    private dateDisabled(ymd, date: Date) {
        // Disable weekends (Sunday = `0`, Saturday = `6`) and
        const weekday = date.getDay();
        // Return `true` if the date should be disabled
        return weekday === 0 || weekday === 6;
    }

    private onCollectionChange(
        halfDayCollection: Array<ICalendarHalfDay>,
        profile: IPlanificationPhaseProfile,
        index: number
    ) {
        if (!profile) {
            return;
        }
        const workDays: IPlanificationPhaseProfileWorkDay[] = Array.from(
            profile.planificationPhaseProfileWorkDays ?? []
        );
        profile.planificationPhaseProfileWorkDays = [];
        if (halfDayCollection && halfDayCollection.length > 0) {
            halfDayCollection.forEach((element) => {
                const oldVal = workDays.filter((x) => {
                    const d = new Date(x.date);
                    return (
                        x.dayPartTypeId === element.dayPartId &&
                        d.getDate() === element.day &&
                        d.getMonth() === element.month &&
                        d.getFullYear() === element.year
                    );
                });
                if (oldVal === undefined || (oldVal && oldVal.length === 0)) {
                    const newObject: IPlanificationPhaseProfileWorkDay = {
                        planificationPhaseProfileId: profile.id,
                        date: new Date(element.year, element.month, element.day),
                        dayPartTypeId: element.dayPartId,
                        id: 0
                    };
                    newObject.date.setHours(0, 0, 0, 0);
                    profile.planificationPhaseProfileWorkDays.push(newObject);
                } else {
                    profile.planificationPhaseProfileWorkDays.push(oldVal[0]);
                }
            });
        }
        this.setDateforProfile(profile, index);
        this.setProfileTotalRate(profile, this.selectedPlanificationPhaseToUpdate, index);
        this.setOpeningAndClosingDateForPhase(this.selectedPlanificationPhaseToUpdate);
    }

    private formattedPhaseTotalRate(item: IPlanificationPhase): string {
        return this.formatAmountWithCurrency(this.getPhaseTotalRate(item));
    }

    formatAmountWithCurrency(value: number): string {
        return formatCurrencyByProject(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }

    private getPhaseTotalRate(item: IPlanificationPhase): number {
        let res = 0;
        item.planificationPhaseProfiles.forEach((element) => {
            res += element.totalRate;
        });
        return res;
    }

    setDateforProfile(profile, index) {
        const phaseToUpdate = this.planificationPhases?.find(
            (p) => p.id === profile.planificationPhaseId || p.id === profile.parentPhaseId
        );
        if (phaseToUpdate) {
            const profileToUpdate = phaseToUpdate.planificationPhaseProfiles[index];
            if (profile.planificationPhaseProfileWorkDays.length === 0 && !this.isAddProfileBouttonClicked) {
                phaseToUpdate.planificationPhaseProfiles.splice(index, 1);
            } else {
                if (profileToUpdate) {
                    profileToUpdate.planificationPhaseProfileWorkDays = profile.planificationPhaseProfileWorkDays;
                    const defaultSelected = profile.planificationPhaseProfileWorkDays.map((x) => {
                        const d: Date = new Date(x.date);
                        return {
                            year: d.getFullYear(),
                            month: d.getMonth(),
                            day: d.getDate(),
                            dayPartId: x.dayPartTypeId
                        } as ICalendarHalfDay;
                    });
                    if (profile.planificationPhaseProfileWorkDays[0]) {
                        const date = new Date(profile.planificationPhaseProfileWorkDays[0].date);
                        profileToUpdate.calendarPropWorkDays = {
                            defaultSelected: defaultSelected,
                            defaultMonth: date.getMonth(),
                            defaultYear: date.getFullYear()
                        };
                    }
                }
            }

            if (phaseToUpdate.planificationPhaseProfiles.length === 0) {
                phaseToUpdate.openingDate = null;
                phaseToUpdate.closingDate = null;
            }
        }
    }

    setProfileTotalRate(profile, phase: IPlanificationPhase, index: number = -1): void {
        const projectEntityLabel = this.selectedProject?.entity.label;
        if (profile.employee != null) {
            if (this.employeeRoles != null) {
                const employeeRoleList = profile.employee?.histos;
                if (employeeRoleList != null) {
                    let totalRate = 0;
                    employeeRoleList?.forEach((employeeRole) => {
                        const eRole = this.employeeRoles?.find((r) => r.id === employeeRole?.employeeRoleId);
                        const eRoleRate = getEmployeCost(eRole, projectEntityLabel!);
                        const planificationPhaseProfileWorkDaysByRole =
                            profile.planificationPhaseProfileWorkDays.filter((day) => {
                                return (
                                    isAfter(
                                        new Date(day.date).setHours(0, 0, 0, 0),
                                        new Date(employeeRole.startDate).setHours(0, 0, 0, 0)
                                    ) ||
                                    isEqual(
                                        new Date(day.date).setHours(0, 0, 0, 0),
                                        new Date(employeeRole.startDate).setHours(0, 0, 0, 0)
                                    )
                                );
                            });
                        totalRate = 0;
                        totalRate += eRoleRate * (planificationPhaseProfileWorkDaysByRole?.length ?? 0);
                    });
                    profile.totalRate = totalRate;
                }
            }
        } else {
            const eRole = this.employeeRoles?.find(
                (x) => x.id === profile.employeeRole?.id || x.label === profile.profileRole
            );
            profile.employeeRoleId = profile.employeeRole?.id;
            profile.totalRate =
                profile.planificationPhaseProfileWorkDays.length * getEmployeCost(eRole!, projectEntityLabel!);
        }
        const phaseToUpdate = this.planificationPhases?.find((p) => p.id === phase.id);
        if (phaseToUpdate) {
            const profileToUpdate = phaseToUpdate.planificationPhaseProfiles[index];
            if (profileToUpdate) {
                profileToUpdate.totalRate = profile.totalRate;
            }
        }
    }

    setOpeningAndClosingDateForPhase(phase: IPlanificationPhase): void {
        const dates: Date[] = [];
        const phaseToUpdate = this.planificationPhases?.find((p) => p.id === phase.id);
        if (phase && phase.planificationPhaseProfiles && phase.planificationPhaseProfiles.length > 0 && phaseToUpdate) {
            phase.planificationPhaseProfiles.forEach((profile) => {
                const itemDates = profile.planificationPhaseProfileWorkDays.map((x) => new Date(x.date));
                dates.push(...itemDates);
            });
            if (dates && dates.length > 0) {
                const minDate = min(dates);
                const maxDate = max(dates);
                minDate.setHours(3);
                maxDate.setHours(3);
                phase.openingDate = minDate.toISOString();
                phase.closingDate = maxDate.toISOString();

                phaseToUpdate.openingDate = phase.openingDate;
                phaseToUpdate.closingDate = phase.closingDate;
            } else {
                phase.openingDate = null;
                phase.closingDate = null;
                phaseToUpdate.openingDate = null;
                phaseToUpdate.closingDate = null;
            }
        } else if (phase.planificationPhaseProfiles.length === 0 && phaseToUpdate) {
            phase.openingDate = null;
            phase.closingDate = null;
            phaseToUpdate.openingDate = null;
            phaseToUpdate.closingDate = null;
        }
    }
}
